/**
 * Semantic Color Tokens for Cassie Material Design
 *
 * This file separates semantic meaning from brand colors, allowing for
 * flexible theming while maintaining consistent user experience patterns.
 *
 * Semantic tokens represent the INTENT/PURPOSE of colors:
 * - What does this color mean to the user?
 * - How should this color behave in different contexts?
 *
 * Brand tokens represent the BRAND IDENTITY:
 * - What colors represent our brand?
 * - What is our visual identity?
 */

import { BRAND_COLORS, BRAND_SCALES, BRAND_NEUTRALS } from "./brandColors";

/**
 * Semantic tokens for UI states and interactions
 * These map semantic meaning to Enterprise SaaS Design System colors
 */
export const SEMANTIC_TOKENS = {
  // Primary actions and emphasis - using steel blue for primary actions
  action: {
    primary: BRAND_COLORS.primary, // #406A94 - Primary CTAs, toggles, radios (steel blue)
    primaryHover: BRAND_SCALES.steelBlue.dark, // #355a80 - Hover state for primary actions
    primaryActive: BRAND_SCALES.steelBlue.dark, // #355a80 - Active state for primary actions
    primaryDisabled: BRAND_NEUTRALS.lightText, // #6b7884 - Disabled primary actions
  },

  // Secondary actions
  action_secondary: {
    default: BRAND_COLORS.tertiary, // #7F8C98 - Secondary buttons (cadet gray)
    hover: BRAND_SCALES.cadetGray.dark, // #6b7884 - Hover state for secondary actions
    active: BRAND_SCALES.cadetGray.dark, // #6b7884 - Active state for secondary actions
    disabled: BRAND_NEUTRALS.lightText, // #6b7884 - Disabled secondary actions
  },

  // Destructive actions - keep red for dangerous/destructive actions
  action_destructive: {
    default: "#dc2626", // Red - Destructive actions (delete, remove)
    hover: "#b91c1c", // Dark red - Hover state for destructive actions
    active: "#b91c1c", // Dark red - Active state for destructive actions
    disabled: BRAND_NEUTRALS.lightText, // #6b7884 - Disabled destructive actions
  },

  // Status and feedback colors
  status: {
    success: "#059669", // Green - Success states, positive feedback
    warning: "#ea580c", // Orange - Warning states, caution
    error: "#dc2626", // Dark red - Error states, destructive actions
    info: "#2563eb", // Blue - Informational states (use sparingly)
  },
  // Text colors following Enterprise SaaS Design System
  text: {
    primary: BRAND_NEUTRALS.darkText, // #3C3C3C - Primary text (Onyx)
    secondary: BRAND_NEUTRALS.mediumText, // #525252 - Secondary text
    tertiary: BRAND_NEUTRALS.lightText, // #6b7884 - Tertiary text (Cadet Gray Dark)
    disabled: BRAND_NEUTRALS.lightText, // #6b7884 - Disabled text
    inverse: BRAND_COLORS.white, // #ffffff - Text on dark backgrounds
    brand: BRAND_COLORS.primary, // #406A94 - Steel Blue accent text
  },

  // Background colors
  background: {
    primary: BRAND_COLORS.white, // #ffffff - Primary content backgrounds
    secondary: BRAND_NEUTRALS.light, // #f0f2f4 - Secondary backgrounds, surfaces
    tertiary: BRAND_NEUTRALS.veryLight, // #f9fafb - Subtle background variations
    overlay: "rgba(60, 60, 60, 0.8)", // Semi-transparent overlay (Onyx-based)
    disabled: BRAND_NEUTRALS.subtleBackground, // #e1e8ed - Disabled component backgrounds
  },

  // Border colors
  border: {
    default: BRAND_NEUTRALS.subtleBorder, // #939fac - Standard borders (Cadet Gray Light)
    strong: BRAND_NEUTRALS.mediumBorder, // #7F8C98 - Emphasized borders (Cadet Gray)
    subtle: BRAND_NEUTRALS.light, // #f0f2f4 - Very subtle borders
    focus: BRAND_COLORS.primary, // #406A94 - Focus indicators (Steel Blue)
    error: "#dc2626", // Error state borders
    success: "#059669", // Success state borders
  },

  // Interactive states
  interactive: {
    hover: BRAND_NEUTRALS.veryLight, // #f9fafb - Hover backgrounds
    active: BRAND_NEUTRALS.subtleBackground, // #e1e8ed - Active/pressed states
    selected: BRAND_SCALES.steelBlue.veryLight, // #e8f0f8 - Selected items (Steel Blue very light)
    focus: BRAND_COLORS.primary, // #406A94 - Focus outlines (Steel Blue)
  },
} as const;

/**
 * Chart semantic tokens - separate from UI semantics
 * These represent data visualization meanings
 */
export const CHART_SEMANTIC_TOKENS = {  // Primary data visualization
  data: {
    primary: BRAND_COLORS.primary, // #406A94 - Most important data series (Steel Blue)
    secondary: "#059669", // Green - Secondary important data
    tertiary: "#ea580c", // Orange - Third level data
    quaternary: BRAND_COLORS.secondary, // #0B3142 - Fourth level (Prussian Blue)
  },
  // Data trends and meanings
  trend: {
    positive: "#059669", // Green - Growth, success, positive trends
    negative: "#dc2626", // Red - Decline, errors, negative trends
    neutral: BRAND_NEUTRALS.darkText, // #3C3C3C - Stable, no change (Onyx)
    warning: "#ea580c", // Orange - Caution, attention needed
  },

  // Chart background elements
  chart: {
    background: BRAND_COLORS.white, // #ffffff - Chart backgrounds
    grid: BRAND_NEUTRALS.light, // #f0f2f4 - Grid lines
    axis: BRAND_NEUTRALS.darkText, // #3C3C3C - Axis lines and labels (Onyx)
    tooltip: BRAND_NEUTRALS.darkText, // #3C3C3C - Tooltip backgrounds (Onyx)
  },
} as const;

/**
 * Component-specific semantic tokens
 * Maps component states to appropriate colors
 */
export const COMPONENT_TOKENS = {
  button: {
    primary: {
      background: SEMANTIC_TOKENS.action.primary, // Charcoal background
      backgroundHover: SEMANTIC_TOKENS.action.primaryHover,
      backgroundActive: SEMANTIC_TOKENS.action.primaryActive,
      backgroundDisabled: SEMANTIC_TOKENS.action.primaryDisabled,
      text: BRAND_COLORS.white, // White text on charcoal
      textDisabled: BRAND_COLORS.white,
    },
    secondary: {
      background: "transparent",
      backgroundHover: SEMANTIC_TOKENS.interactive.hover,
      backgroundActive: SEMANTIC_TOKENS.interactive.active,
      backgroundDisabled: "transparent",
      text: SEMANTIC_TOKENS.action_secondary.default,
      textDisabled: SEMANTIC_TOKENS.action_secondary.disabled,
      border: SEMANTIC_TOKENS.border.default,
      borderHover: SEMANTIC_TOKENS.border.strong,
    },
    destructive: {
      background: SEMANTIC_TOKENS.action_destructive.default, // Red for destructive actions
      backgroundHover: SEMANTIC_TOKENS.action_destructive.hover,
      backgroundActive: SEMANTIC_TOKENS.action_destructive.active,
      backgroundDisabled: SEMANTIC_TOKENS.action_destructive.disabled,
      text: BRAND_COLORS.white,
      textDisabled: BRAND_COLORS.white,
    },
  },

  input: {
    background: SEMANTIC_TOKENS.background.primary,
    backgroundDisabled: SEMANTIC_TOKENS.background.disabled,
    text: SEMANTIC_TOKENS.text.primary,
    textDisabled: SEMANTIC_TOKENS.text.disabled,
    placeholder: SEMANTIC_TOKENS.text.tertiary,
    border: SEMANTIC_TOKENS.border.default,
    borderFocus: SEMANTIC_TOKENS.border.focus,
    borderError: SEMANTIC_TOKENS.border.error,
    borderSuccess: SEMANTIC_TOKENS.border.success,
  },

  card: {
    background: SEMANTIC_TOKENS.background.primary,
    backgroundHover: SEMANTIC_TOKENS.interactive.hover,
    border: SEMANTIC_TOKENS.border.subtle,
    borderHover: SEMANTIC_TOKENS.border.default,
    shadow: "rgba(36, 37, 41, 0.1)",
  },
} as const;

// Type exports for TypeScript support
export type SemanticToken = keyof typeof SEMANTIC_TOKENS;
export type ChartSemanticToken = keyof typeof CHART_SEMANTIC_TOKENS;
export type ComponentToken = keyof typeof COMPONENT_TOKENS;
